import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError, catchError, switchMap, BehaviorSubject, filter, take } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { environment } from '../../../environments/environment';

let isRefreshing = false;
let refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const SKIP_AUTH_HEADER = 'X-Skip-Auth';

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn) => {
  const authService = inject(AuthService);
  const isApiUrl = req.url.startsWith(environment.apiUrl);
  const skipAuth = req.headers.has(SKIP_AUTH_HEADER);

  // Strip the opt-out header so it never leaves the browser.
  if (skipAuth) {
    req = req.clone({ headers: req.headers.delete(SKIP_AUTH_HEADER) });
  }

  // Exclude token generation/refresh endpoints from being intercepted
  const isAuthCreateOrRefresh = req.url.includes('/api/auth/jwt/create/') || req.url.includes('/api/auth/jwt/refresh/');

  if (isApiUrl && !skipAuth && authService.isAuthenticated && !isAuthCreateOrRefresh) {
    req = addToken(req, authService.accessToken);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // If unauthorized and we haven't just failed the login/refresh endpoint itself
      if (error.status === 401 && isApiUrl && !skipAuth && !isAuthCreateOrRefresh) {
        return handle401Error(req, next, authService);
      }
      return throwError(() => error);
    })
  );
};

function addToken(request: HttpRequest<any>, token: string) {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });
}

function handle401Error(request: HttpRequest<any>, next: HttpHandlerFn, authService: AuthService) {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refresh = authService.refreshToken;
    if (refresh) {
      return authService.refreshAccessToken(refresh).pipe(
        switchMap((res: any) => {
          isRefreshing = false;
          refreshTokenSubject.next(res.access);
          return next(addToken(request, res.access));
        }),
        catchError((err) => {
          isRefreshing = false;
          authService.logout();
          return throwError(() => err);
        })
      );
    } else {
      isRefreshing = false;
      authService.logout();
    }
  }

  // If another request comes in while we're refreshing, wait for the subject to be populated
  return refreshTokenSubject.pipe(
    filter(token => token !== null),
    take(1),
    switchMap((token) => next(addToken(request, token as string)))
  );
}
